/** venture/CertPlanBox.tsx — 14단계 카드 상단: 발급 방식(복수) 표시 · 변경 · 이력 (2026-09-24)
 *  한 회사에 방식을 여러 개 고를 수 있다(예: 조합 인수 전환사채 + 자본금 증자분 → 확인서 2장).
 *  바꾸면 이력 한 줄이 남고, 뺀 방식으로 채운 항목은 접혀서 보관된다(지우지 않는다). */
import React, { useState } from 'react';
import { CERT_MODES, CERT_SHORT, certModesLabel, planModes, isCapitalMode } from './types';
import type { Project, CoCard, CertMode, CapitalRound } from './types';
import { certPlanOf, patchRegCo, regCoOf } from './coterms';
import { setCertModes, previousModes, modesText } from './certplan';
import { kitRows } from './kit13';

interface Props { p: Project; co: CoCard; onPatch: (patch: Partial<Project>) => void; toast: (m: string) => void; }
const BLANK: CapitalRound = { date: '', amount: '', taxYear: '' };

export default function CertPlanBox({ p, co, onPatch, toast }: Props) {
  const rc = regCoOf(p, co);
  const plan = certPlanOf(rc);
  const modes = planModes(plan);
  const [editing, setEditing] = useState(false);
  const [to, setTo] = useState<CertMode[]>(modes);
  const [memo, setMemo] = useState('');
  const [showPrev, setShowPrev] = useState(false);
  const save = (patch: Parameters<typeof patchRegCo>[2]) => {
    const pp = patchRegCo(p, co, patch);
    if (!pp) { toast('등록정보에서 이 법인을 찾을 수 없습니다 — 등록정보 탭에서 법인명·사업자번호를 확인해 주세요'); return; }
    onPatch(pp);
  };
  const commit = () => {
    if (!to.length) { toast('방식을 하나 이상 골라 주세요'); return; }
    const next = setCertModes(plan, to, memo);
    if (next === plan) { setEditing(false); return; }
    save({ certPlan: next });
    setEditing(false); setMemo('');
    toast(certModesLabel(modes) + ' → ' + certModesLabel(planModes(next)) + ' 로 변경 · 뺀 방식의 항목은 보관됩니다');
  };
  const rounds: CapitalRound[] = plan.capital.rounds;
  const setRounds = (rs: CapitalRound[]) => save({ certPlan: { ...plan, capital: { rounds: rs } } });
  const prev = previousModes(plan);
  const last = plan.history[plan.history.length - 1];
  const hasCapital = modes.some(isCapitalMode);
  const roundLabel = (i: number): string => (modes.indexOf('capital_new') >= 0 && modes.indexOf('capital_inc') < 0 ? '설립 출자' : (i + 1) + '회차 증자');

  return (
    <div className="cpbox">
      <div className="cpbox-h">
        <span className="tl">발급 방식</span>
        {modes.length ? modes.map((m) => <span key={m} className="pill p-blue" title={CERT_MODES.filter((x) => x[0] === m).map((x) => x[1])[0]}>{CERT_SHORT[m]}</span>) : <span className="pill p-amber">미선택</span>}
        {modes.length > 1 ? <span className="s2tag">확인서 {modes.length}장</span> : null}
        {last ? <span className="s3muted">{last.at.slice(0, 10)} {modesText(last.from, CERT_SHORT)} → {modesText(last.to, CERT_SHORT)}{last.memo ? ' · ' + last.memo : ''}</span> : null}
        <span className="grow" />
        {!editing ? <button className="rbtn" onClick={() => { setTo(modes); setEditing(true); }}>{modes.length ? '방식 변경' : '방식 선택'}</button> : null}
      </div>
      {editing ? (
        <div className="cpbox-edit">
          <div className="snote" style={{ marginBottom: 6 }}>복수 선택 가능 — 예: 조합 인수 전환사채 + 자본금 증자분이면 둘 다 켭니다. 방식마다 서류 묶음과 확인서가 따로 나옵니다.</div>
          <div className="modes">
            {CERT_MODES.map(([m, l, d]) => (
              <button type="button" key={m} className={'mode' + (to.indexOf(m) >= 0 ? ' on' : '')} onClick={() => setTo(to.indexOf(m) >= 0 ? to.filter((x) => x !== m) : [...to, m])}>
                <b>{to.indexOf(m) >= 0 ? '☑ ' : '☐ '}{l}</b><span>{d}</span>
              </button>
            ))}
          </div>
          <div className="s3file" style={{ gap: 8 }}>
            <input className="cotext" value={memo} onChange={(e) => setMemo(e.target.value)} placeholder="변경 사유 (선택) — 예: 대표님 요청, 증자분 추가" />
            <button className="rbtn ok" onClick={commit}>확정</button>
            <button className="rbtn" onClick={() => setEditing(false)}>취소</button>
          </div>
        </div>
      ) : null}
      {hasCapital ? (
        <div className="rounds">
          {(rounds.length ? rounds : [BLANK]).map((r, i, arr) => (
            <div className="terms-row" key={i}>
              <span className="tl">{roundLabel(i)}</span>
              <input className="cotext" type="date" value={r.date} onChange={(e) => setRounds(arr.map((x, j) => (j === i ? { ...x, date: e.target.value } : x)))} style={{ maxWidth: 150 }} />
              <input className="cotext amt" value={r.amount} onChange={(e) => setRounds(arr.map((x, j) => (j === i ? { ...x, amount: e.target.value.replace(/[^0-9,]/g, '') } : x)))} placeholder="증자금액" />
              <span className="tu">원</span>
              <span className="tl" style={{ minWidth: 0 }}>귀속연도</span>
              <input className="cotext" value={r.taxYear} onChange={(e) => setRounds(arr.map((x, j) => (j === i ? { ...x, taxYear: e.target.value.replace(/[^0-9]/g, '').slice(0, 4) } : x)))} placeholder="2026" style={{ maxWidth: 90 }} />
              {arr.length > 1 ? <button className="rbtn danger" onClick={() => setRounds(arr.filter((_, j) => j !== i))}>삭제</button> : null}
            </div>
          ))}
          <button className="rbtn" onClick={() => setRounds([...(rounds.length ? rounds : [BLANK]), BLANK])}>+ 회차 추가</button>
        </div>
      ) : null}
      {modes.indexOf('cb_direct') >= 0 ? (
        <div className="terms-row">
          <span className="tl">재인수 예정자</span>
          <input className="cotext" value={plan.direct.buyer} onChange={(e) => save({ certPlan: { ...plan, direct: { ...plan.direct, buyer: e.target.value } } })} placeholder={(rc && rc.ceo) ? rc.ceo + ' (대표자)' : '대표자명'} style={{ maxWidth: 160 }} />
          <span className="tl" style={{ minWidth: 0 }}>예정 시기</span>
          <input className="cotext" value={plan.direct.when} onChange={(e) => save({ certPlan: { ...plan, direct: { ...plan.direct, when: e.target.value } } })} placeholder="예: 벤처확인 후 1개월 내" />
        </div>
      ) : null}
      {prev.length ? (
        <div className="prevmodes">
          <button className="rbtn" onClick={() => setShowPrev(!showPrev)}>
            {showPrev ? '▴' : '▾'} 뺀 방식의 보관 항목 · {prev.map((m) => CERT_SHORT[m]).join(' · ')}
          </button>
          {showPrev ? prev.map((m) => {
            const rows = kitRows(p, co, m).filter((r) => r.done && r.src === 'manual');
            return (
              <div key={m} className="snote" style={{ marginTop: 6 }}>
                <b>{CERT_SHORT[m]}</b> — 직접 채운 항목 {rows.length}건{rows.length ? ': ' + rows.map((r) => r.item.label + (r.item.kind === 'file' ? ' (' + r.own + ')' : '')).join(', ') : ''}. 방식을 다시 켜면 그대로 복구됩니다.
              </div>
            );
          }) : null}
        </div>
      ) : null}
    </div>
  );
}
